<template>
    <div class="ngm__qr-reader-container">
        <qrcode-stream 
            v-if="renderComponent"
            :camera="camera"
            :track="paintBoundingBox" 
            @init="onInit"
            @decode="onDecode">
            <div v-if="cameraError || validationPending || isLoggedIn" class="camera-overlay-message">
                <template v-if="cameraError">
                    <div>{{ cameraError }}</div>
                    <button class="btn btn-default" @click="forceRerender">Retry</button>
                </template>
                <template v-if="validationPending && !isLoggedIn">Verifying QR Code</template>

                <template v-if="isLoggedIn">
                    <success-check></success-check>
                    <div>Success!</div>
                </template>          
            </div>
        </qrcode-stream>

        <div class="footer">
            <div class="footer-instruction">
                <span>Hold QR code up to camera.</span>
                <a :href="`${routes.support_url}/tutorials/login.html#login-with-qr-code-students`" target="_blank">
                    <font-awesome-icon 
                        class="float-right" 
                        :icon="'question-circle'" 
                        v-tooltip="'Use your computer\'s built-in camera to scan your personal QR code. If no image is displayed, verify that the browser has access to the camera and click \'retry\'. Click here for more.'" />
                </a>
            </div>
            <div v-if="errorMessage" class="error-field text-danger">{{ errorMessage }}</div>
        </div>        
    </div>
</template>

<script>
    import { QrcodeStream, QrcodeDropZone, QrcodeCapture } from 'vue-qrcode-reader';
    import SuccessCheck from '@root/js/components/SuccessCheck.vue';

    export default {
        data: () => ({
            camera: 'auto',
            result: null,
            errorMessage: null,
            cameraError: null,
            renderComponent: true,
            isLoggedIn: false,
            redirectUrl: null,
        }),
        computed: {
            validationPending () {
                return this.isValid === undefined && this.camera === 'off'
            },

            validationSuccess () {
                return this.isValid === true
            },

            validationFailure () {
                return this.isValid === false
            },
        },
        methods: {
            async onInit (promise) {
                this.cameraError = null;

                try {
                    await promise
                } catch (error) {
                    if (error.name === 'NotAllowedError') {
                        this.cameraError = "ERROR: you need to grant camera access permission."
                    } else if (error.name === 'NotFoundError') {
                        this.cameraError = "ERROR: no camera on this device."
                    } else if (error.name === 'NotSupportedError') {
                        this.cameraError = "ERROR: secure context required (HTTPS, localhost)."
                    } else if (error.name === 'NotReadableError') {
                        this.cameraError = "ERROR: is the camera already in use?"
                    } else if (error.name === 'OverconstrainedError') {
                        this.cameraError = "ERROR: installed cameras are not suitable."
                    } else if (error.name === 'StreamApiNotSupportedError') {
                        this.cameraError = "ERROR: Stream API is not supported in this browser."
                    } else if (error.name === 'InsecureContextError') {
                        this.cameraError = 'ERROR: Camera access is only permitted in secure context. Use HTTPS or localhost rather than HTTP.';
                    } else {
                        this.cameraError = `ERROR: Camera error (${error.name}).`;
                    }
                }
            },
            async onDecode (content) {
                this.result = content;
                this.turnCameraOff();

                // pretend it's taking really long
                await this.timeout(1200);

                this.attemptLogin();
            },

            turnCameraOn () {
                this.camera = 'auto'
            },

            turnCameraOff () {
                this.camera = 'off'
            },

            timeout (ms) {
                return new Promise(resolve => {
                    window.setTimeout(resolve, ms)
                })
            },
            paintBoundingBox(detectedCodes, ctx) {
                for (const detectedCode of detectedCodes) {
                    const { boundingBox: { x, y, width, height } } = detectedCode

                    ctx.lineWidth = 2
                    ctx.strokeStyle = '#007bff'
                    ctx.strokeRect(x, y, width, height)
                }
            },
            attemptLogin() {
                console.log('attempting login');

                axios.post(`${this.routes.api_url}/v1.0/login/badgeReader`, {
                    data: this.result,
                }).then(response => {
                    console.log('response', response);

                    if (response.data.status && response.data.status == 'failed') {
                        this.turnCameraOn();
                        return this.errorMessage = response.data.message;
                    }
                    
                    let redirectRegex = new RegExp('^(' + this.routes.ngm_url + ')');
                    
                    if (this.referrer && redirectRegex.test(this.referrer)) {
                        this.redirectUrl = this.referrer;
                        this.isLoggedIn = true;
                        return;
                    }

                    if (response.data.redirect_uri && redirectRegex.test(response.data.redirect_uri)) {
                        this.redirectUrl = response.data.redirect_uri;
                        this.isLoggedIn = true;
                        return;
                    }

                    this.redirectUrl = this.routes.ngm_url + '/assignments';
                    this.isLoggedIn = true;
                }, error => {
                    this.turnCameraOn();
                    return this.errorMessage = 'An unexpected error occurred. Please try again.';
                });
            },
            forceRerender() {
                this.renderComponent = false;

                this.$nextTick().then(() => {
                    this.renderComponent = true;
                });
            },
        },
        watch: {
            async isLoggedIn(val) {
                if (val) {
                    await this.timeout(2000);
                    window.location.href = this.redirectUrl;
                }
            },
        },
        components: {
            QrcodeStream,
            QrcodeDropZone,
            QrcodeCapture,
            SuccessCheck,
        },
    }
</script>

<style scoped>
    .ngm__qr-reader-container >>> .qrcode-stream-wrapper {
        /* width: 500px; */
        height: 357px;
        background-color: #000;
    }
    
    .ngm__qr-reader-container >>> .qrcode-stream-overlay {
        display: flex;
        justify-content: center;
        align-items: center;
        /* position: relative; */
    }
    
    .camera-overlay-message {
        padding: 8px;
        font-size: 1.8rem;
        color: #fff;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 8px;
        text-align: center;
        max-width: 280px;
    }

    .footer {
        text-align: center;
        padding: 8px;
    }

    .footer-instruction {
        width: 100%;
        line-height: 1.6rem;
        vertical-align: middle;
        font-size: 1.6rem;
    }

    .error-field {
        font-size: 1.2rem;
        width: 100%;
        padding-top: 4px;
    }

    .error-field::before {
        content: "\200b";
    }
</style>
